// src/pages/NdviPage.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import AppShell from "../components/AppShell.jsx";
import { SATELLITE_STYLE } from "../utils/mapTerrain.js";
import { apiFetchJson, getStoredUser } from "../api/api.js";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt2(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "—";
}

function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = String(s).split("-");
  if (!y || !m || !d) return s;
  return `${d}.${m}.${y}`;
}

function fmtDateTime(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return s;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function parseMaybeJson(v) {
  if (!v) return null;
  if (typeof v === "object") return v;
  try { return JSON.parse(v); } catch { return null; }
}

function extractGeometry(f) {
  return (
    f?.geometry ??
    parseMaybeJson(f?.geometryJson) ??
    parseMaybeJson(f?.contour) ??
    parseMaybeJson(f?.shape) ??
    null
  );
}

function getFieldCenter(geo) {
  if (!geo) return [31, 48];
  const g = geo.type === "Feature" ? geo.geometry : geo;
  let coords = [];
  if (g?.type === "Polygon") coords = g.coordinates?.flat(1) ?? [];
  if (g?.type === "MultiPolygon") coords = g.coordinates?.flat(2) ?? [];
  if (!coords.length) return [31, 48];
  const lngs = coords.map(c => c[0]);
  const lats  = coords.map(c => c[1]);
  return [(Math.min(...lngs) + Math.max(...lngs)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2];
}

function getFieldBbox(geo) {
  const g = geo?.type === "Feature" ? geo.geometry : geo;
  let coords = [];
  if (g?.type === "Polygon") coords = g.coordinates?.flat(1) ?? [];
  if (g?.type === "MultiPolygon") coords = g.coordinates?.flat(2) ?? [];
  if (!coords.length) return null;
  const lngs = coords.map(c => c[0]);
  const lats  = coords.map(c => c[1]);
  return [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]];
}

// Standard NDVI → hex color (red→yellow→green)
function ndviToHex(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0)  return "#c0392b";
  if (n <= 0.15) return "#e74c3c";
  if (n <= 0.25) return "#e67e22";
  if (n <= 0.35) return "#f39c12";
  if (n <= 0.45) return "#f1c40f";
  if (n <= 0.55) return "#d4e157";
  if (n <= 0.65) return "#8bc34a";
  if (n <= 0.75) return "#4caf50";
  if (n <= 0.85) return "#2e7d32";
  return "#1b5e20";
}

// NDVI card background (subtle)
function ndviCardBg(mean) {
  const v = Number(mean);
  if (!Number.isFinite(v)) return "var(--surface-soft)";
  if (v < 0.2)  return "rgba(231,76,60,0.14)";
  if (v < 0.4)  return "rgba(243,156,18,0.14)";
  if (v < 0.6)  return "rgba(212,225,87,0.14)";
  return "rgba(76,175,80,0.16)";
}

// Verbal NDVI interpretation in Ukrainian
function ndviLabel(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0)  return { text: "Гола поверхня / вода",        color: "#e74c3c" };
  if (n <= 0.2) return { text: "Дуже розріджена рослинність",   color: "#e67e22" };
  if (n <= 0.35) return { text: "Розріджена рослинність",        color: "#f39c12" };
  if (n <= 0.5) return { text: "Помірна рослинність",            color: "#8bc34a" };
  if (n <= 0.65) return { text: "Добра рослинність",             color: "#4caf50" };
  if (n <= 0.8) return { text: "Густа рослинність",              color: "#2e7d32" };
  return                { text: "Дуже густа рослинність",        color: "#1b5e20" };
}

function initMapboxToken() {
  if (!mapboxgl.accessToken) {
    const t = process.env.REACT_APP_MAPBOX_TOKEN || "";
    if (t) mapboxgl.accessToken = t;
  }
  return !!mapboxgl.accessToken;
}

// ─── StatusBadge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const st = String(status || "").toUpperCase();
  let bg, color, border, label;
  if (st === "DONE") {
    bg = "rgba(40,163,91,0.13)"; color = "var(--g)"; border = "rgba(40,163,91,0.28)"; label = "DONE";
  } else if (st === "FAILED") {
    bg = "rgba(228,88,121,0.13)"; color = "var(--danger)"; border = "rgba(228,88,121,0.28)"; label = "FAILED";
  } else {
    bg = "rgba(215,155,22,0.13)"; color = "var(--warn)"; border = "rgba(215,155,22,0.28)"; label = st || "PENDING";
  }
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 20,
      fontSize: 12, fontWeight: 700, letterSpacing: "0.04em",
      background: bg, color, border: `1px solid ${border}`,
    }}>
      {label}
    </span>
  );
}

// ─── NDVI Map ─────────────────────────────────────────────────────────────────

function NdviMap({ geometry, result }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !geometry || !result) return;
    if (!initMapboxToken()) return;

    const geo = geometry.type === "Feature" ? geometry.geometry : geometry;
    const center = getFieldCenter(geometry);
    const bbox   = getFieldBbox(geometry);
    const color  = ndviToHex(result.ndviMean);

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: SATELLITE_STYLE,
      center,
      zoom: 12,
      attributionControl: false,
    });
    mapRef.current = map;

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

    map.on("load", () => {
      map.addSource("ndvi-field", {
        type: "geojson",
        data: { type: "Feature", geometry: geo, properties: {} },
      });

      // Colored fill — NDVI mean color at 70% opacity
      map.addLayer({
        id: "ndvi-fill",
        type: "fill",
        source: "ndvi-field",
        paint: { "fill-color": color, "fill-opacity": 0.68 },
      });

      // White outline
      map.addLayer({
        id: "ndvi-outline",
        type: "line",
        source: "ndvi-field",
        paint: { "line-color": "#ffffff", "line-width": 2.5, "line-opacity": 0.9 },
      });

      if (bbox) {
        map.fitBounds(bbox, { padding: 60, duration: 600 });
      }

      // Popup on click
      map.on("click", "ndvi-fill", () => {
        const lbl = ndviLabel(result.ndviMean);
        new mapboxgl.Popup({ closeButton: true, maxWidth: "260px" })
          .setLngLat(center)
          .setHTML(`
            <div style="font-family:sans-serif;padding:4px 2px">
              <div style="font-weight:700;font-size:14px;margin-bottom:8px">NDVI Результат</div>
              <div style="font-size:12px;color:#555;margin-bottom:2px">Мінімум: <b>${fmt2(result.ndviMin)}</b></div>
              <div style="font-size:12px;color:#555;margin-bottom:2px">Максимум: <b>${fmt2(result.ndviMax)}</b></div>
              <div style="font-size:12px;color:#555;margin-bottom:2px">Середнє: <b style="color:${lbl.color}">${fmt2(result.ndviMean)}</b></div>
              <div style="font-size:12px;color:#555;margin-bottom:6px">Відхилення: <b>${fmt2(result.ndviStdDev)}</b></div>
              <div style="font-size:12px;font-weight:600;color:${lbl.color}">${lbl.text}</div>
              <div style="font-size:11px;color:#888;margin-top:4px">${fmtDate(result.dateFrom)} — ${fmtDate(result.dateTo)}</div>
            </div>
          `)
          .addTo(map);
      });

      map.on("mouseenter", "ndvi-fill", () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", "ndvi-fill", () => { map.getCanvas().style.cursor = ""; });
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [geometry, result]);

  const interp = ndviLabel(result?.ndviMean);

  return (
    <div style={{ position: "relative", borderRadius: "var(--r16)", overflow: "hidden" }}>
      <div ref={containerRef} style={{ width: "100%", height: 420 }} />

      {/* ── Legend ── */}
      <div style={{
        position: "absolute", bottom: 24, left: 16,
        background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)",
        borderRadius: 12, padding: "12px 14px", minWidth: 200,
        color: "#fff",
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", marginBottom: 8, opacity: 0.8 }}>
          ІНДЕКС ВЕГЕТАЦІЇ (NDVI)
        </div>
        <div style={{
          height: 12, borderRadius: 6, marginBottom: 6,
          background: "linear-gradient(to right, #c0392b, #e67e22, #f1c40f, #d4e157, #8bc34a, #2e7d32, #1b5e20)",
        }} />
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, opacity: 0.75, marginBottom: 10 }}>
          <span>0</span><span>0.25</span><span>0.5</span><span>0.75</span><span>1.0</span>
        </div>
        <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 2 }}>Середнє значення:</div>
        <div style={{ fontSize: 20, fontWeight: 900, color: ndviToHex(result?.ndviMean), letterSpacing: "-0.02em" }}>
          {fmt2(result?.ndviMean)}
        </div>
        <div style={{ fontSize: 11, color: interp.color, fontWeight: 600, marginTop: 2 }}>
          {interp.text}
        </div>
      </div>

      {/* ── Attribution ── */}
      <div style={{
        position: "absolute", bottom: 8, right: 10,
        fontSize: 10, color: "rgba(255,255,255,0.5)",
      }}>
        © Google Satellite
      </div>
    </div>
  );
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────

function DetailModal({ rec, onClose }) {
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  if (!rec) return null;

  const interp = ndviLabel(rec.ndviMean);
  const rows = [
    ["Поле ID",      rec.fieldId],
    ["Дата від",     fmtDate(rec.dateFrom)],
    ["Дата до",      fmtDate(rec.dateTo)],
    ["NDVI Мін",     fmt2(rec.ndviMin)],
    ["NDVI Макс",    fmt2(rec.ndviMax)],
    ["NDVI Середнє", <span key="m" style={{ color: interp.color, fontWeight: 800 }}>{fmt2(rec.ndviMean)}</span>],
    ["NDVI Відхил.", fmt2(rec.ndviStdDev)],
    ["Оцінка",       <span key="i" style={{ color: interp.color, fontWeight: 600 }}>{interp.text}</span>],
    ["Статус",       <StatusBadge key="s" status={rec.status} />],
    ["Розраховано",  fmtDateTime(rec.calculatedAt)],
    ...(rec.errorMessage ? [["Помилка", <span key="e" style={{ color: "var(--danger)" }}>{rec.errorMessage}</span>]] : []),
  ];

  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9000,
        background: "var(--modal-overlay)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--modal-bg)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r18)",
          boxShadow: "var(--shadow-strong)",
          padding: "28px 28px 24px",
          width: "100%", maxWidth: 440,
          maxHeight: "90vh", overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ fontWeight: 800, fontSize: 17 }}>Деталі розрахунку #{rec.id}</div>
          <button type="button" onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "var(--muted)", lineHeight: 1, padding: "2px 6px" }}>
            ✕
          </button>
        </div>

        {/* Mini color indicator */}
        <div style={{
          borderRadius: "var(--r12)", padding: "12px 16px", marginBottom: 20,
          background: ndviCardBg(rec.ndviMean),
          border: `1px solid ${ndviToHex(rec.ndviMean)}44`,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8, flexShrink: 0,
            background: ndviToHex(rec.ndviMean),
          }} />
          <div>
            <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>NDVI Середнє</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: ndviToHex(rec.ndviMean), lineHeight: 1.1 }}>{fmt2(rec.ndviMean)}</div>
            <div style={{ fontSize: 12, color: interp.color, fontWeight: 600 }}>{interp.text}</div>
          </div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {rows.map(([lbl, val]) => (
              <tr key={lbl}>
                <td style={{
                  padding: "8px 16px 8px 0", color: "var(--muted)", fontSize: 13,
                  borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", verticalAlign: "top",
                }}>{lbl}</td>
                <td style={{
                  padding: "8px 0", fontWeight: 600, fontSize: 14,
                  borderBottom: "1px solid var(--border)", wordBreak: "break-word",
                }}>{val ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
          <button type="button" className="btn" onClick={onClose} style={{ minWidth: 90 }}>Закрити</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function NdviPage() {
  const navigate = useNavigate();
  const [user] = useState(() => getStoredUser());

  useEffect(() => {
    if (String(user?.role || "").toUpperCase() !== "ADMIN") {
      navigate("/main", { replace: true });
    }
  }, [user, navigate]);

  const [fields,      setFields]      = useState([]);
  const [fieldId,     setFieldId]     = useState("");
  const [dateFrom,    setDateFrom]    = useState("");
  const [dateTo,      setDateTo]      = useState("");
  const [calculating, setCalculating] = useState(false);
  const [calcError,   setCalcError]   = useState(null);
  const [result,      setResult]      = useState(null);
  const [history,     setHistory]     = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histError,   setHistError]   = useState(null);
  const [modalRec,    setModalRec]    = useState(null);

  // Load fields list
  useEffect(() => {
    if (!user) return;
    apiFetchJson(user, "/api/fields").then(({ ok, data }) => {
      if (ok && Array.isArray(data)) setFields(data);
    });
  }, [user]);

  const loadHistory = useCallback(async (fid) => {
    if (!fid || !user) return;
    setHistLoading(true);
    setHistError(null);
    const { ok, data, error } = await apiFetchJson(user, `/api/ndvi/field/${fid}`);
    setHistLoading(false);
    if (ok && Array.isArray(data)) {
      setHistory([...data].sort((a, b) => {
        const ta = new Date(a.calculatedAt || a.dateFrom || 0).getTime();
        const tb = new Date(b.calculatedAt || b.dateFrom || 0).getTime();
        return tb - ta;
      }));
    } else {
      setHistError(error || "Не вдалось завантажити історію");
      setHistory([]);
    }
  }, [user]);

  useEffect(() => {
    if (fieldId) { setResult(null); loadHistory(fieldId); }
    else          { setHistory([]); setHistError(null); }
  }, [fieldId, loadHistory]);

  async function handleCalculate(e) {
    e.preventDefault();
    if (!fieldId || !dateFrom || !dateTo) return;
    setCalculating(true);
    setCalcError(null);
    setResult(null);

    const { ok, data, error } = await apiFetchJson(user, "/api/ndvi/calculate", {
      method: "POST",
      body: { fieldId: Number(fieldId), dateFrom, dateTo },
    });

    setCalculating(false);
    if (ok && data) {
      setResult(data);
      loadHistory(fieldId);
    } else {
      setCalcError(error || "Помилка розрахунку");
    }
  }

  const selectedField = fields.find((f) => String(f.id) === String(fieldId));
  const fieldGeometry = selectedField ? extractGeometry(selectedField) : null;
  const interp        = result ? ndviLabel(result.ndviMean) : null;

  // ── Styles ──
  const card  = { background: "var(--panel)", border: "1px solid var(--border)", borderRadius: "var(--r18)", boxShadow: "var(--shadow-soft)", padding: "24px" };
  const lbl   = { display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--muted)", textTransform: "uppercase", marginBottom: 6 };
  const inp   = { width: "100%", boxSizing: "border-box", background: "var(--input-bg,var(--surface))", border: "1px solid var(--border)", borderRadius: "var(--r12)", padding: "10px 14px", fontSize: 14, color: "var(--text)", outline: "none", appearance: "none" };

  return (
    <AppShell title="NDVI Моніторинг" subtitle="Розрахунок вегетаційного індексу полів">
      <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 1100, margin: "0 auto" }}>

        {/* ── Form ── */}
        <div style={card}>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 20 }}>Розрахунок NDVI</div>
          <form onSubmit={handleCalculate}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
              <div>
                <label style={lbl}>Поле</label>
                <select value={fieldId} onChange={(e) => setFieldId(e.target.value)} required style={{ ...inp, cursor: "pointer" }}>
                  <option value="">— Оберіть поле —</option>
                  {fields.map((f) => <option key={f.id} value={f.id}>{f.name || `Поле #${f.id}`}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Дата від</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} required style={inp} />
              </div>
              <div>
                <label style={lbl}>Дата до</label>
                <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} required style={inp} />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <button type="submit" className="btn primary" disabled={calculating || !fieldId || !dateFrom || !dateTo} style={{ minWidth: 160 }}>
                {calculating ? "Розраховуємо..." : "🌿 Розрахувати"}
              </button>
              {calculating && <span style={{ fontSize: 13, color: "var(--muted)", fontStyle: "italic" }}>Отримуємо дані з супутника...</span>}
            </div>
          </form>
          {calcError && (
            <div style={{ marginTop: 16, background: "rgba(228,88,121,0.10)", border: "1px solid rgba(228,88,121,0.28)", borderRadius: "var(--r12)", padding: "12px 16px", color: "var(--danger)", fontSize: 14 }}>
              {calcError}
            </div>
          )}
        </div>

        {/* ── Result ── */}
        {result && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Stat cards */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>Результат</div>
              <StatusBadge status={result.status} />
              {interp && <span style={{ fontSize: 13, color: interp.color, fontWeight: 600 }}>— {interp.text}</span>}
            </div>

            <div className="ndvi-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
              {[
                { key: "ndviMin",    label: "NDVI Мінімум",   value: result.ndviMin },
                { key: "ndviMax",    label: "NDVI Максимум",  value: result.ndviMax },
                { key: "ndviMean",   label: "NDVI Середнє",   value: result.ndviMean },
                { key: "ndviStdDev", label: "NDVI Відхилення", value: result.ndviStdDev },
              ].map(({ key, label: kl, value }) => (
                <div key={key} style={{
                  background: key === "ndviMean" ? ndviCardBg(result.ndviMean) : "var(--panel)",
                  border: `1px solid ${key === "ndviMean" ? ndviToHex(result.ndviMean) + "44" : "var(--border)"}`,
                  borderRadius: "var(--r16)", padding: "20px 18px", textAlign: "center", boxShadow: "var(--shadow-soft)",
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--muted)", textTransform: "uppercase", marginBottom: 8 }}>{kl}</div>
                  <div style={{ fontSize: 28, fontWeight: 900, color: key === "ndviMean" ? ndviToHex(result.ndviMean) : "var(--text)", lineHeight: 1 }}>{fmt2(value)}</div>
                </div>
              ))}
            </div>

            {result.errorMessage && (
              <div style={{ background: "rgba(228,88,121,0.10)", border: "1px solid rgba(228,88,121,0.28)", borderRadius: "var(--r12)", padding: "12px 16px", color: "var(--danger)", fontSize: 14 }}>
                {result.errorMessage}
              </div>
            )}

            {/* Map */}
            {fieldGeometry && (
              <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                <div style={{ padding: "16px 20px 12px", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontWeight: 800, fontSize: 15 }}>Карта NDVI</span>
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>
                    — {selectedField?.name || `Поле #${fieldId}`} · {fmtDate(result.dateFrom)} – {fmtDate(result.dateTo)}
                  </span>
                </div>
                <NdviMap geometry={fieldGeometry} result={result} />
                <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)", fontSize: 12, color: "var(--muted)" }}>
                  Натисніть на поле, щоб побачити детальну інформацію. Колір відображає середнє значення NDVI за вибраний період.
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── History ── */}
        {fieldId && (
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>
                Історія NDVI{selectedField ? ` — ${selectedField.name || `Поле #${selectedField.id}`}` : ""}
              </div>
              {!histLoading && (
                <button type="button" className="btn" onClick={() => loadHistory(fieldId)} style={{ fontSize: 13, padding: "6px 14px" }}>
                  ↻ Оновити
                </button>
              )}
            </div>

            {histLoading  && <div style={{ color: "var(--muted)", fontSize: 14, padding: "20px 0" }}>Завантажую...</div>}
            {histError && !histLoading && <div style={{ color: "var(--danger)", fontSize: 14 }}>{histError}</div>}
            {!histLoading && !histError && history.length === 0 && <div style={{ color: "var(--muted)", fontSize: 14, padding: "20px 0" }}>Записів немає.</div>}

            {!histLoading && history.length > 0 && (
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                  <thead>
                    <tr>
                      {["Дата від","Дата до","Мін","Макс","Середнє","Статус"].map((h) => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--muted)", textTransform: "uppercase", borderBottom: "2px solid var(--border)", whiteSpace: "nowrap" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((row, i) => (
                      <tr key={row.id ?? i} onClick={() => setModalRec(row)} style={{ cursor: "pointer" }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "var(--surface-hover)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                        <td style={{ padding: "10px 12px", fontSize: 14, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" }}>{fmtDate(row.dateFrom)}</td>
                        <td style={{ padding: "10px 12px", fontSize: 14, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" }}>{fmtDate(row.dateTo)}</td>
                        <td style={{ padding: "10px 12px", fontSize: 14, borderBottom: "1px solid var(--border)", fontWeight: 600 }}>{fmt2(row.ndviMin)}</td>
                        <td style={{ padding: "10px 12px", fontSize: 14, borderBottom: "1px solid var(--border)", fontWeight: 600 }}>{fmt2(row.ndviMax)}</td>
                        <td style={{ padding: "10px 12px", fontSize: 14, borderBottom: "1px solid var(--border)", fontWeight: 700, color: ndviToHex(row.ndviMean) }}>{fmt2(row.ndviMean)}</td>
                        <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--border)" }}><StatusBadge status={row.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {!fieldId && (
          <div style={{ ...card, textAlign: "center", color: "var(--muted)", fontSize: 14, padding: "40px 24px" }}>
            Оберіть поле для перегляду NDVI-аналізу та історії
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 767px) { .ndvi-stat-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 479px) { .ndvi-stat-grid { grid-template-columns: 1fr !important; } }
      `}</style>

      {modalRec && <DetailModal rec={modalRec} onClose={() => setModalRec(null)} />}
    </AppShell>
  );
}
